/**
 * Converts text to URL-friendly slug
 * @param {string} text - Text to convert to slug
 * @returns {string} - URL-friendly slug
 */
export const slugify = (text) => {
  if (!text) return '';
  
  return text
    .toString()
    .toLowerCase()
    .trim()
    // Remove acentos portugueses
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Replace spaces with hyphens
    .replace(/\s+/g, '-')
    // Remove special characters except hyphens
    .replace(/[^\w-]+/g, '')
    // Replace multiple hyphens with single hyphen
    .replace(/--+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '');
};

/**
 * Generates SEO-friendly product URL
 * @param {object} product - Product object
 * @returns {string} - SEO-friendly URL path
 */
export const generateProductUrl = (product) => {
  if (!product) return '';
  
  const categorySlug = slugify(product.category || 'produto');
  const nameSlug = slugify(product.name || 'produto');
  const productCode = product.code || product.id || 'sem-codigo';
  
  return `/produtos/${categorySlug}/${nameSlug}-${productCode}`;
};

/**
 * Extracts product ID from SEO-friendly URL
 * @param {string} url - SEO-friendly URL
 * @returns {string|null} - Product ID/code
 */
export const extractProductIdFromUrl = (url) => {
  // Pattern: /produtos/categoria/nome-produto-CODIGO
  const match = url.match(/\/produtos\/[^/]+\/.*-([^-]+)$/);
  return match ? match[1] : null;
};

/**
 * Generates breadcrumb data from product URL
 * @param {string} pathname - Current pathname
 * @param {object} product - Product object (optional)
 * @returns {array} - Breadcrumb items
 */
export const generateProductBreadcrumbs = (pathname, product = null) => {
  const breadcrumbs = [
    { name: 'Início', href: '/' }
  ];
  
  // Check if it's a product page
  if (pathname.startsWith('/produtos/')) {
    breadcrumbs.push({ name: 'Produtos', href: '/shop' });
    
    const parts = pathname.split('/');
    if (parts.length >= 3) {
      // Add category
      const categorySlug = parts[2];
      const categoryName = categorySlug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      breadcrumbs.push({ 
        name: categoryName, 
        href: `/shop?category=${categoryName}` 
      });
      
      // Add product name (current page)
      if (product) {
        breadcrumbs.push({ 
          name: product.name, 
          href: null 
        });
      } else if (parts.length >= 4) {
        // Extract product name from URL
        const productSlug = parts[3];
        const productName = productSlug
          .split('-')
          .slice(0, -1) // Remove the last part (product code)
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        breadcrumbs.push({ 
          name: productName || 'Produto', 
          href: null 
        });
      }
    }
  }
  
  return breadcrumbs;
};
